import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Form, Icon, Label, Modal, TextArea, Select } from 'semantic-ui-react';
import { RiChatSmileAiLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';

import selectors from '../../selectors';
import Config from '../../constants/Config';
import GoogleDriveModal from '../CardModal/AttachmentAddZone/GoogleDriveModal';

import styles from './AgentPromptModal.module.scss';

const intervalOptions = [
  { key: '0', text: 'Disabled', value: 0 },
  { key: '240', text: 'Every 4 hours', value: 240 },
  { key: '480', text: 'Every 8 hours', value: 480 },
  { key: '720', text: 'Every 12 hours', value: 720 },
  { key: '1440', text: 'Every 24 hours', value: 1440 },
];

const AgentPromptModal = React.memo(
  ({
    isOpen,
    entityType, // 'project', 'board', 'list'
    agentType, // 'arrival', 'timer'
    entityName,
    systemPrompt,
    agentEnabled,
    scheduleInterval,
    canonDocument,
    projectId,
    onSave,
    onClose,
  }) => {
    const accessToken = useSelector(selectors.selectAccessToken);
    const [prompt, setPrompt] = useState(systemPrompt || '');
    const [enabled, setEnabled] = useState(agentEnabled || false);
    const [interval, setIntervalValue] = useState(scheduleInterval || 0);
    const [isSaving, setIsSaving] = useState(false);
    const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
    const [canon, setCanon] = useState(canonDocument || null);
    const [isSettingCanon, setIsSettingCanon] = useState(false);

    React.useEffect(() => {
      setPrompt(systemPrompt || '');
      setEnabled(agentEnabled || false);
      setIntervalValue(scheduleInterval || 0);
      setCanon(canonDocument || null);
    }, [systemPrompt, agentEnabled, scheduleInterval, canonDocument]);

    const handleSetCanon = useCallback(
      async file => {
        if (!projectId || !accessToken) return;
        setIsSettingCanon(true);
        try {
          const response = await fetch(
            `${Config.SERVER_BASE_URL}/api/projects/${projectId}/canon`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              credentials: 'include',
              body: JSON.stringify({
                driveFileId: file.id,
                driveFileName: file.name,
                driveMimeType: file.mimeType,
              }),
            }
          );

          if (response.ok) {
            const data = await response.json();
            setCanon({
              fileName: data.canonDriveFileName,
              syncedAt: data.canonSyncedAt,
            });
          } else {
            console.error('Failed to set canon document:', response.status);
          }
        } catch (error) {
          console.error('Error setting canon document:', error);
        } finally {
          setIsSettingCanon(false);
        }
      },
      [projectId, accessToken]
    );

    const handleRemoveCanon = useCallback(async () => {
      if (!projectId || !accessToken) return;
      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/projects/${projectId}/canon`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        });

        if (response.ok) {
          setCanon(null);
        }
      } catch (error) {
        console.error('Error removing canon document:', error);
      }
    }, [projectId, accessToken]);

    const handlePromptChange = useCallback(e => {
      setPrompt(e.target.value);
    }, []);

    const handleEnabledToggle = useCallback(() => {
      setEnabled(!enabled);
    }, [enabled]);

    const handleIntervalChange = useCallback((e, { value }) => {
      setIntervalValue(value);
    }, []);

    const handleSave = useCallback(async () => {
      setIsSaving(true);
      try {
        await onSave({
          systemPrompt: prompt,
          agentEnabled: enabled,
          scheduleInterval: interval,
        });
        onClose();
      } catch (error) {
        console.error('Error saving agent config:', error);
      } finally {
        setIsSaving(false);
      }
    }, [prompt, enabled, interval, onSave, onClose]);

    // If inline is requested, render as a simple div structure (good for embeddings)
    // BUT: To fix the clipping issue in the List Popup, it's better to force it to be a Modal (Portal)
    // so it breaks out of the overflow:hidden of the small popup.
    // So we will IGNORE the inline prop if we want to fix the visual bug described by the user.

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        size="small"
        closeIcon
        className={styles.modal}
        mountNode={document.body} // Force mount at body level to escape any parent overflow
      >
        <Modal.Header className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <RiChatSmileAiLine className={styles.icon} />
              AI Agent Configuration
            </div>
            <div className={styles.subtitle}>
              {entityType === 'project' && 'Project:'}
              {entityType === 'board' && 'Board:'}
              {entityType === 'list' && 'List:'} {entityName}
            </div>
          </div>
        </Modal.Header>

        <Modal.Content className={styles.content}>
          <div className={styles.contentModule}>
            <Form>
              <Form.Field>
                <Checkbox
                  label="Enable AI Agent"
                  checked={enabled}
                  onChange={handleEnabledToggle}
                  className={styles.checkbox}
                />
                <p className={styles.helpText}>
                  When enabled, the AI agent will monitor and take actions in this {entityType}.
                </p>
              </Form.Field>

              {entityType === 'project' && (
                <Form.Field>
                  <label className={styles.label}>
                    <Icon name="book" />
                    Canon Document (Knowledge Base)
                  </label>
                  {canon && canon.fileName ? (
                    <div className={styles.canonDisplay}>
                      <Label size="large" className={styles.canonLabel}>
                        <Icon name="file pdf outline" />
                        {canon.fileName}
                        {canon.syncedAt && (
                          <Label.Detail>
                            Synced {new Date(canon.syncedAt).toLocaleDateString()}
                          </Label.Detail>
                        )}
                      </Label>
                      <div className={styles.canonActions}>
                        <Button
                          size="tiny"
                          basic
                          color="blue"
                          onClick={() => setIsDriveModalOpen(true)}
                          loading={isSettingCanon}
                          disabled={isSettingCanon}
                        >
                          <Icon name="exchange" />
                          Replace
                        </Button>
                        <Button size="tiny" basic color="red" onClick={handleRemoveCanon}>
                          <Icon name="trash" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      basic
                      color="blue"
                      onClick={() => setIsDriveModalOpen(true)}
                      loading={isSettingCanon}
                      disabled={isSettingCanon}
                    >
                      <Icon name="google drive" />
                      Select Canon Document from Drive
                    </Button>
                  )}
                  <p className={styles.helpText}>
                    Select one master document from Google Drive. It will be uploaded to Gemini and
                    refreshed automatically when the project is opened.
                  </p>
                </Form.Field>
              )}

              {entityType === 'list' && (
                <Form.Field>
                  <label className={styles.label}>
                    <Icon name="clock" />
                    Execution Schedule (Timer)
                  </label>
                  <Select
                    placeholder="Select interval"
                    options={intervalOptions}
                    value={interval}
                    onChange={handleIntervalChange}
                    disabled={!enabled}
                  />
                  <p className={styles.helpText}>
                    Define how often the agent should analyze ALL cards in this list. (Set to
                    "Disabled" to only run on card arrival).
                  </p>
                </Form.Field>
              )}

              <Form.Field>
                <label className={styles.label}>
                  <Icon name="terminal" />
                  System Prompt (Instructions)
                </label>
                <TextArea
                  rows={12}
                  placeholder={`Enter the instructions for this agent...\n\nExample:\n"If a card says 'pickup' and there's less than 1 hour left, send a slack notification."`}
                  value={prompt}
                  onChange={handlePromptChange}
                  className={styles.textarea}
                  disabled={!enabled}
                  // CRITICAL FIX: Prevent clicks inside textarea from propagating to parent popup
                  onClick={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                />
                <p className={styles.helpText}>
                  Both Arrival and Timer agents use the same prompt instructions.
                </p>
              </Form.Field>
            </Form>
          </div>

          <div className={styles.infoBox}>
            <Icon name="info circle" />
            <div>
              <strong>Agent Capabilities (Future):</strong>
              <ul>
                <li>Move cards between lists automatically</li>
                <li>Add/remove labels based on content</li>
                <li>Set due dates and priorities</li>
                <li>Create sub-tasks</li>
                <li>Send notifications</li>
              </ul>
            </div>
          </div>
        </Modal.Content>

        <Modal.Actions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button primary loading={isSaving} disabled={isSaving} onClick={handleSave}>
            <Icon name="save" />
            Save Configuration
          </Button>
        </Modal.Actions>

        {isDriveModalOpen && (
          <GoogleDriveModal
            onClose={() => setIsDriveModalOpen(false)}
            onSelect={file => {
              handleSetCanon(file);
              setIsDriveModalOpen(false);
            }}
            forKnowledgeBase
          />
        )}
      </Modal>
    );
  }
);

AgentPromptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  entityType: PropTypes.oneOf(['project', 'board', 'list']).isRequired,
  agentType: PropTypes.oneOf(['arrival', 'timer']),
  entityName: PropTypes.string.isRequired,
  systemPrompt: PropTypes.string,
  agentEnabled: PropTypes.bool,
  scheduleInterval: PropTypes.number,
  canonDocument: PropTypes.object,
  projectId: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

AgentPromptModal.defaultProps = {
  agentType: 'arrival',
  systemPrompt: '',
  agentEnabled: false,
  scheduleInterval: 0,
  canonDocument: null,
  projectId: null,
};

export default AgentPromptModal;
